import React, { useEffect, useState } from "react"
import moment, { Moment } from "moment"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { Printer } from "lucide-react"

import PageContainer from "~/library/PageContainer"
import BookingServiceReport from "~/views/Reports/BookingServiceReport"
import KitchenProductionReport from "~/views/Reports/KitchenProductionReport"
import PayoutReconciliationReport from "~/views/Reports/PayoutReconciliationReport"
import PageTitle from "~/library/PageTitle"
import { useReportSearchParams } from "~/hooks/useReportSearchParams"
import getAppConfig from "~/utils/getAppConfig"

type TimeRange = "today" | "week" | "month" | "custom"
type ReportType = "booking-service" | "kitchen-production" | "payout-reconciliation"

const baseReportTypeOptions: { label: string; value: ReportType }[] = [
  { label: "Booking Service", value: "booking-service" },
  { label: "Kitchen Production Report", value: "kitchen-production" },
]

const timeRangeOptions = [
  { label: "Today", value: "today" },
  { label: "This Week", value: "week" },
  { label: "This Month", value: "month" },
  { label: "Custom Range", value: "custom" },
]

const ReportsView = () => {
  const { user } = getAppConfig()
  const { type, range, startDate, endDate, setReportSearchParams } = useReportSearchParams()

  const requestedReportType = (type as ReportType) || "booking-service"
  const reportType =
    user.role === "internal" || requestedReportType !== "payout-reconciliation"
      ? requestedReportType
      : "booking-service"
  const timeRange = (range as TimeRange) || "week"
  const customStartDate = startDate ? moment(startDate) : null
  const customEndDate = endDate ? moment(endDate) : null

  const [kitchenHasItems, setKitchenHasItems] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)
  const reportTypeOptions =
    user.role === "internal"
      ? [
          ...baseReportTypeOptions,
          { label: "Payout Reconciliation", value: "payout-reconciliation" as ReportType },
        ]
      : baseReportTypeOptions

  useEffect(() => {
    if (!type || !range) {
      setReportSearchParams({
        type: type || ("booking-service" as ReportType),
        range: range || ("week" as TimeRange),
      })
    }
  }, [type, range, setReportSearchParams])

  useEffect(() => {
    if (user.role !== "internal" && type === "payout-reconciliation") {
      setReportSearchParams({ type: "booking-service" })
    }
  }, [type, user.role, setReportSearchParams])

  useEffect(() => {
    setIsPrinting(false)
  }, [startDate, endDate, timeRange])

  const getDateRangeFor = (range: TimeRange) => {
    switch (range) {
      case "today":
        return {
          startDate: moment().startOf("day").format("YYYY-MM-DD"),
          endDate: moment().endOf("day").format("YYYY-MM-DD"),
        }
      case "week":
        return {
          startDate: moment().startOf("week").format("YYYY-MM-DD"),
          endDate: moment().endOf("week").format("YYYY-MM-DD"),
        }
      case "month":
        return {
          startDate: moment().startOf("month").format("YYYY-MM-DD"),
          endDate: moment().endOf("month").format("YYYY-MM-DD"),
        }
      case "custom":
        return startDate && endDate ? { startDate, endDate } : null
      default:
        return {
          startDate: moment().startOf("week").format("YYYY-MM-DD"),
          endDate: moment().endOf("week").format("YYYY-MM-DD"),
        }
    }
  }

  const handleReportTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setReportSearchParams({ type: e.target.value as ReportType })
  }

  const handleTimeRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRange = e.target.value as TimeRange
    if (newRange !== "custom") {
      setReportSearchParams({ range: newRange, startDate: "", endDate: "" })
    } else {
      setReportSearchParams({ range: newRange })
    }
  }

  const handleCustomStartChange = (value: Moment | null) => {
    setReportSearchParams({
      range: "custom",
      startDate: value ? value.format("YYYY-MM-DD") : "",
    })
  }

  const handleCustomEndChange = (value: Moment | null) => {
    setReportSearchParams({
      range: "custom",
      endDate: value ? value.format("YYYY-MM-DD") : "",
    })
  }

  const dateRange = getDateRangeFor(timeRange)

  const renderReportContent = () => {
    switch (reportType) {
      case "booking-service":
        return <BookingServiceReport {...dateRange} />
      case "kitchen-production":
        return dateRange ? (
          <KitchenProductionReport
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            isPrinting={isPrinting}
            onAfterPrint={() => setIsPrinting(false)}
            onItemsChange={setKitchenHasItems}
          />
        ) : null
      case "payout-reconciliation":
        return (
          <PayoutReconciliationReport
            startDate={dateRange?.startDate}
            endDate={dateRange?.endDate}
          />
        )
      default:
        return (
          <div className="card">
            <div className="card-body text-center py-5">
              <h5 className="text-muted">Report type not implemented yet</h5>
            </div>
          </div>
        )
    }
  }

  const showRangeControls = [
    "booking-service",
    "kitchen-production",
    "payout-reconciliation",
  ].includes(reportType)

  return (
    <PageContainer>
      <PageTitle title="Reports">
        <div className="d-flex gap-2 align-items-center">
          <select
            className="form-select"
            value={reportType}
            onChange={handleReportTypeChange}
            style={{ width: "220px" }}
          >
            {reportTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {showRangeControls && (
            <>
              <select
                className="form-select"
                value={timeRange}
                onChange={handleTimeRangeChange}
                style={{ maxWidth: "160px" }}
              >
                {timeRangeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              {timeRange === "custom" && (
                <>
                  <DatePicker
                    label="From"
                    format="MM/DD/YYYY"
                    sx={{ width: 180 }}
                    value={customStartDate}
                    maxDate={customEndDate || undefined}
                    slotProps={{ field: { size: "small" } }}
                    onChange={handleCustomStartChange}
                  />
                  <DatePicker
                    label="To"
                    format="MM/DD/YYYY"
                    sx={{ width: 180 }}
                    value={customEndDate}
                    minDate={customStartDate || undefined}
                    slotProps={{ field: { size: "small" } }}
                    onChange={handleCustomEndChange}
                  />
                </>
              )}
            </>
          )}

          {reportType === "kitchen-production" && (
            <button
              type="button"
              className="btn btn-secondary d-flex"
              onClick={() => setIsPrinting(true)}
              disabled={!kitchenHasItems}
            >
              <Printer size={16} class="me-1" />
              Print
            </button>
          )}
        </div>
      </PageTitle>

      {renderReportContent()}
    </PageContainer>
  )
}

export default ReportsView
